import React from 'react';
import { Document, Page, View, Text, StyleSheet, Image } from '@react-pdf/renderer';
import { Expense, EXPENSE_CATEGORIES, Vehicle, expenseAffectsStoreCash } from '../../types';

const toNum = (v: unknown): number => {
  if (typeof v === 'number' && !Number.isNaN(v)) return v;
  const s = String(v).trim();
  if (s === '') return 0;
  if (/,\d*$/.test(s)) return Number(s.replace(/\./g, '').replace(',', '.')) || 0;
  return parseFloat(s) || 0;
};

const formatCurrency = (value: unknown): string =>
  toNum(value).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const formatDateBR = (dateString?: string): string => {
  if (!dateString) return '-';
  const d = String(dateString).split('T')[0];
  const [y, m, day] = d.split('-');
  if (!y || !m || !day) return d;
  return `${day}/${m}/${y}`;
};

const truncate = (v: unknown, max: number): string => {
  const s = String(v ?? '').replace(/\s+/g, ' ').trim();
  if (!s) return '';
  if (s.length <= max) return s;
  return `${s.slice(0, Math.max(0, max - 1)).trimEnd()}…`;
};

/** Quebra palavras longas (ex.: AAAAA...) para não estourar a coluna no react-pdf. */
const breakLongWords = (text: string, chunkSize = 24): string => {
  if (!text) return text;
  return text
    .split(' ')
    .map((token) => {
      if (token.length <= chunkSize) return token;
      const parts: string[] = [];
      for (let i = 0; i < token.length; i += chunkSize) parts.push(token.slice(i, i + chunkSize));
      return parts.join(' ');
    })
    .join(' ');
};

const styles = StyleSheet.create({
  page: { padding: 32, fontSize: 10, fontFamily: 'Helvetica', color: '#111827' },
  headerRow: { flexDirection: 'row', justifyContent: 'space-between', marginBottom: 14, gap: 12, alignItems: 'center' },
  logo: { width: 140, height: 36, objectFit: 'contain' },
  title: { fontSize: 16, fontWeight: 700 },
  subtitle: { marginTop: 4, color: '#6B7280' },
  vehicleBox: { borderWidth: 1, borderColor: '#E5E7EB', borderRadius: 8, padding: 10, marginBottom: 14 },
  vehicleTop: { flexDirection: 'row', gap: 10 },
  vehicleImg: { width: 140, height: 90, objectFit: 'cover', borderRadius: 6 },
  vehicleImgPlaceholder: { width: 140, height: 90, borderRadius: 6, backgroundColor: '#F3F4F6' },
  vehicleInfo: { flexGrow: 1 },
  vehicleName: { fontSize: 12, fontWeight: 700 },
  vehicleMeta: { marginTop: 4, color: '#374151' },
  totalsRow: { flexDirection: 'row', justifyContent: 'space-between', marginTop: 10, paddingTop: 10, borderTopWidth: 1, borderTopColor: '#E5E7EB' },
  totalsLabel: { color: '#6B7280' },
  totalsValue: { fontWeight: 700 },
  table: { borderWidth: 1, borderColor: '#E5E7EB', borderRadius: 8, overflow: 'hidden' },
  rowHeader: { flexDirection: 'row', backgroundColor: '#F9FAFB', borderBottomWidth: 1, borderBottomColor: '#E5E7EB', paddingVertical: 8, paddingHorizontal: 8 },
  row: { flexDirection: 'column', borderBottomWidth: 1, borderBottomColor: '#F3F4F6', paddingVertical: 7, paddingHorizontal: 8 },
  rowTop: { flexDirection: 'row', alignItems: 'flex-start' },
  cellDate: { width: 70 },
  cellCat: { width: 90 },
  cellDesc: { flexGrow: 1, minWidth: 0, paddingRight: 8 },
  cellValue: { width: 85, textAlign: 'right', fontWeight: 700 },
  cellCash: { width: 70, textAlign: 'right' },
  note: { marginTop: 2, color: '#6B7280' },
  descText: { fontSize: 10, lineHeight: 1.25 },
  noteText: { fontSize: 9, lineHeight: 1.2, color: '#6B7280' },
});

export interface VehicleExpensesPDFProps {
  vehicle: Vehicle;
  expenses: Expense[];
  generatedAtLabel: string;
  vehicleImageUrl?: string | null;
  logoUrl?: string | null;
}

const getVehicleIdLabel = (v: Vehicle): string => {
  if ((v as any).identification) return String((v as any).identification);
  if ((v as any).no_plate) return 'Sem placa';
  return String((v as any).plate || '—');
};

const VehicleExpensesPDF: React.FC<VehicleExpensesPDFProps> = ({ vehicle, expenses, generatedAtLabel, vehicleImageUrl, logoUrl }) => {
  const totalAll = expenses.reduce((sum, e) => sum + toNum(e.amount), 0);
  const totalAffectCash = expenses.reduce((sum, e) => sum + (expenseAffectsStoreCash(e) ? toNum(e.amount) : 0), 0);
  const purchaseCost = vehicle.is_consignado ? 0 : toNum((vehicle as any).purchase_price);
  const totalCost = purchaseCost + totalAll;

  const sorted = [...expenses].sort((a, b) => String(b.expense_date || '').localeCompare(String(a.expense_date || '')));

  return (
    <Document title="Despesas do veículo">
      <Page size="A4" style={styles.page}>
        <View style={styles.headerRow}>
          {logoUrl ? <Image src={logoUrl} style={styles.logo} /> : <View />}
          <View>
            <Text style={styles.title}>Despesas do veículo</Text>
            <Text style={styles.subtitle}>Gerado em: {generatedAtLabel}</Text>
          </View>
          <View>
            <Text style={styles.subtitle}>Total itens: {sorted.length}</Text>
          </View>
        </View>

        <View style={styles.vehicleBox}>
          <View style={styles.vehicleTop}>
            {vehicleImageUrl ? (
              <Image src={vehicleImageUrl} style={styles.vehicleImg} />
            ) : (
              <View style={styles.vehicleImgPlaceholder} />
            )}
            <View style={styles.vehicleInfo}>
              <Text style={styles.vehicleName}>
                {vehicle.brand} {vehicle.model} ({vehicle.year})
              </Text>
              <Text style={styles.vehicleMeta}>Identificação: {getVehicleIdLabel(vehicle)}</Text>
              <Text style={styles.vehicleMeta}>Cor: {vehicle.color || '—'}</Text>
              <Text style={styles.vehicleMeta}>
                Compra: {vehicle.purchase_date ? formatDateBR(vehicle.purchase_date) : '—'} • Preço compra:{' '}
                {vehicle.is_consignado ? 'N/A' : formatCurrency(vehicle.purchase_price)}
              </Text>
              {vehicle.is_consignado && <Text style={styles.vehicleMeta}>Consignado: Sim</Text>}
            </View>
          </View>

          <View style={styles.totalsRow}>
            <Text style={styles.totalsLabel}>Total despesas (todas):</Text>
            <Text style={styles.totalsValue}>{formatCurrency(totalAll)}</Text>
          </View>
          <View style={styles.totalsRow}>
            <Text style={styles.totalsLabel}>Total que desconta do caixa:</Text>
            <Text style={styles.totalsValue}>{formatCurrency(totalAffectCash)}</Text>
          </View>
          <View style={styles.totalsRow}>
            <Text style={styles.totalsLabel}>Custo de compra:</Text>
            <Text style={styles.totalsValue}>{vehicle.is_consignado ? 'N/A' : formatCurrency(purchaseCost)}</Text>
          </View>
          <View style={styles.totalsRow}>
            <Text style={styles.totalsLabel}>Custo total (compra + despesas):</Text>
            <Text style={styles.totalsValue}>{vehicle.is_consignado ? formatCurrency(totalAll) : formatCurrency(totalCost)}</Text>
          </View>
        </View>

        <View style={styles.table}>
          <View style={styles.rowHeader}>
            <Text style={styles.cellDate}>Data</Text>
            <Text style={styles.cellCat}>Categoria</Text>
            <Text style={styles.cellValue}>Valor</Text>
            <Text style={styles.cellCash}>Caixa</Text>
          </View>

          {sorted.map((e) => (
            <View key={e.id} style={styles.row} wrap={false}>
              <View style={styles.rowTop}>
                <Text style={styles.cellDate}>{formatDateBR(e.expense_date)}</Text>
                <Text style={styles.cellCat}>{EXPENSE_CATEGORIES[e.category] ?? String(e.category)}</Text>
                <Text style={styles.cellValue}>{formatCurrency(e.amount)}</Text>
                <Text style={styles.cellCash}>{expenseAffectsStoreCash(e) ? 'Desconta' : 'Não'}</Text>
              </View>
              <View style={{ marginTop: 3 }}>
                <Text style={styles.descText}>{breakLongWords(truncate(e.description || '—', 220))}</Text>
                {e.notes ? <Text style={styles.noteText}>Obs.: {breakLongWords(truncate(e.notes, 140))}</Text> : null}
              </View>
            </View>
          ))}
        </View>
      </Page>
    </Document>
  );
};

export default VehicleExpensesPDF;

